import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CreateVoucherContainer,
  FormContainer,
  FormField,
  FormLabel,
  FormInput,
  ErrorMessage,
  DiscountTypeSection,
  DiscountTypeButtons,
  DiscountTypeButton,
  ReviewButton,
  ReviewModalOverlay,
  ReviewModalContent,
  ReviewModalHeader,
  ReviewModalTitle,
  ReviewModalCloseButton,
  ReviewSection,
  ReviewSectionTitle,
  ReviewDetailRow,
  ReviewDetailLabel,
  ReviewDetailValue,
  ReviewModalActions,
  ReviewBackButton,
  ReviewConfirmButton,
} from "../../styles/DiscountVouchers.styled";
import { POST } from "../../services/HTTPServices";
import { useErrorResponse } from "../../services/useErrorResponse";
import { ALERT_TITLES, ROUTES } from "../../constants";
import {
  alertMessage,
  alertMessageWithCancel,
} from "../../services/alertService";
import {
  LoadingOverlay,
  LoadingSpinner,
  LoadingSpinnerIcon,
} from "../../styles/loading";
import { FlexContainerResponsive } from "../../styles/PersonalInfoStep";
import { LanguageText } from "../../styles";
import { PageTitle } from "../../styles/MenuManagement.styled";
import { SendButton } from "../../styles/AddNewFoodMenu.styled";
import { ActionButtons } from "../../styles/AddNewBundleDeal.styled";
import moment from "moment";
import VoucherDateTime from "../../components/common/CustomDateTimePicker";
interface VoucherFormData {
  id: string;
  serviceProviderId: string;
  name: string;
  description: string;
  type: "amount" | "percentage";
  discountValue: string;
  amount: number;
  miniumAmount: number;
  startTime: string;
  endTime: string;
  status: string;
  isActive: number;
}
type FormErrors = {
  [K in keyof VoucherFormData]?: string;
};

const CreateVoucher: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = id !== undefined && id !== "create";
  const outErrorResponse = useErrorResponse();
  const [loading, setLoading] = React.useState(false);
  const [auth, setAuth] = React.useState<any>(null);

  const [formData, setFormData] = useState<VoucherFormData>({
    id: "",
    serviceProviderId: "",
    name: "",
    description: "",
    type: "amount",
    discountValue: "",
    amount: 0,
    miniumAmount: 0,
    startTime: "",
    endTime: "",
    status: "",
    isActive: 0,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [pastDateWarning, setPastDateWarning] = useState({
    startTime: "",
    endTime: "",
  });

  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

  useEffect(() => {
    const voucher = JSON.parse(
      localStorage.getItem("selected_voucher") || "null"
    );
    if (voucher) {
      setFormData(voucher);
    }

    const authUser = JSON.parse(localStorage.getItem("authUser") || "null");
    if (authUser) {
      setAuth(authUser);
    }
  }, []);

  const submit = async () => {
    let url = isEditing ? "voucher/UpdateVoucher" : "voucher/CreateVoucher";

    let param;
    if (isEditing) {
      param = {
        id: formData.id || "",
        serviceProviderId: auth?.id?.toString() || "",
        name: formData.name || "",
        description: formData.description || "",
        type: formData.type || "amount",
        amount: formData.amount || 0,
        miniumAmount: formData.miniumAmount || 0,
        startTime: formData.startTime || "",
        endTime: formData.endTime || "",
        status: formData.status || "ongoing",
        isActive: formData.isActive || 0,
      };
    } else {
      param = {
        serviceProviderId: auth?.id?.toString() || "",
        name: formData.name || "",
        description: formData.description || "",
        type: formData.type || "amount",
        amount: formData.amount || 0,
        miniumAmount: formData.miniumAmount || 0,
        startTime: formData.startTime || "",
        endTime: formData.endTime || "",
        status: formData.status || "ongoing",
        isActive: formData.isActive || 0,
      };
    }

    let res: any = await POST(url, param, "order");
    if (res && res.status === 200) {
      alertMessage(
        ALERT_TITLES.SUCCESS,
        isEditing ? "Voucher updated." : "Voucher created."
      );
      if (isEditing) localStorage.removeItem("selected_voucher");
      setLoading(false);
      navigate("/discount-vouchers");
    } else {
      setLoading(false);
      outErrorResponse(
        res.status,
        res.message
        // isEditing
        //   ? "Failed to update voucher. Please try again."
        //   : "Failed to create voucher. Please try again."
      );
    }
  };

  const handleInputChange = (field: keyof VoucherFormData, value: string) => {
    if (field === "amount" || field === "miniumAmount") {
      // Validate decimal input - prevent more than 2 decimal places
      let validValue = value;

      // Check if it has more than 2 decimal places
      if (value.includes(".")) {
        const parts = value.split(".");
        if (parts[1] && parts[1].length > 2) {
          validValue = parts[0] + "." + parts[1].substring(0, 2);
        }
      }

      let numValue = parseFloat(validValue) || 0;

      // Prevent percentage values > 100
      if (
        field === "amount" &&
        formData.type === "percentage" &&
        numValue > 100
      ) {
        numValue = 100;
      }

      setFormData((prev) => ({
        ...prev,
        [field]: numValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleDiscountTypeChange = (type: "amount" | "percentage") => {
    setFormData((prev) => ({
      ...prev,
      type: type,
      amount: 0, // Reset value when changing type
    }));
  };

  const getMinDateTime = (): string => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 15); // 15 minutes from now
    return now.toISOString().slice(0, 15);
  };

  const isPastDate = (dateTimeLocal: string): boolean => {
    if (!dateTimeLocal) return false;
    const selectedDate = new Date(dateTimeLocal);
    const now = new Date();
    const minAllowedTime = new Date(now.getTime() + 15 * 60 * 1000); // 15 minutes from now
    return selectedDate < minAllowedTime;
  };

  const handleDateTimeInputChange = (
    field: "startTime" | "endTime",
    value: string
  ) => {
    // Check for past date or less than 15 minutes from now
    if (value && isPastDate(value)) {
      setPastDateWarning((prev) => ({
        ...prev,
        [field]: "Start time must be at least 15 minutes from now.",
      }));
    } else {
      setPastDateWarning((prev) => ({
        ...prev,
        [field]: "",
      }));
    }

    // Store the datetime-local value directly
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Real-time validation for start/end date comparison
    const updatedFormData = { ...formData, [field]: value };
    
    if (field === "startTime" && updatedFormData.endTime) {
      // When start time is updated, check if it's >= end time
      const startDateTime = new Date(value);
      const endDateTime = new Date(updatedFormData.endTime);
      
      if (startDateTime >= endDateTime) {
        setErrors((prev) => ({
          ...prev,
          endTime: "End date/time must be after start date/time.",
        }));
      } else {
        // Clear end time error if it was related to date comparison
        setErrors((prev) => {
          const newErrors = { ...prev };
          if (prev.endTime === "End date/time must be after start date/time.") {
            delete newErrors.endTime;
          }
          return newErrors;
        });
      }
    }
    
    if (field === "endTime" && updatedFormData.startTime) {
      // When end time is updated, check if it's <= start time
      const startDateTime = new Date(updatedFormData.startTime);
      const endDateTime = new Date(value);
      
      if (startDateTime >= endDateTime) {
        setErrors((prev) => ({
          ...prev,
          endTime: "End date/time must be after start date/time.",
        }));
      } else {
        // Clear end time error if it was related to date comparison
        setErrors((prev) => {
          const newErrors = { ...prev };
          if (prev.endTime === "End date/time must be after start date/time.") {
            delete newErrors.endTime;
          }
          return newErrors;
        });
      }
    }

    // Clear error when user selects valid date (for other validation types)
    if (errors[field] && errors[field] !== "End date/time must be after start date/time.") {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // Helper function to validate decimal input in real-time
  const validateDecimalInput = (value: string): string => {
    if (!value) return "";

    // Remove any non-numeric characters except decimal point
    let cleanValue = value.replace(/[^0-9.]/g, "");

    // Ensure only one decimal point
    const parts = cleanValue.split(".");
    if (parts.length > 2) {
      cleanValue = parts[0] + "." + parts.slice(1).join("");
    }

    // Limit to 2 decimal places
    if (parts.length === 2 && parts[1].length > 2) {
      cleanValue = parts[0] + "." + parts[1].substring(0, 2);
    }

    return cleanValue;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Voucher name is required.";
    }

    if (!formData.amount) {
      newErrors.amount =
        "This field cannot be left blank. Please enter a value.";
    } else {
      const value = formData.amount;
      if (isNaN(value) || value <= 0) {
        newErrors.amount = "Please enter a valid positive number.";
      }
      if (formData.type === "percentage" && value > 100) {
        newErrors.amount = "Percentage cannot exceed 100%.";
      }
    }

    const value = formData.miniumAmount;
    if (isNaN(value) || value < 0) {
      newErrors.miniumAmount = "Please enter a valid amount.";
    }

    if (!formData.startTime) {
      newErrors.startTime = "Start date/time is required.";
    }

    if (formData.startTime && isPastDate(formData.startTime)) {
      newErrors.startTime = "Start time must be at least 15 minutes from now.";
    }

    if (!formData.endTime) {
      newErrors.endTime = "End date/time is required.";
    }

    // Validate that start time is less than end time
    if (formData.startTime && formData.endTime) {
      const startDateTime = new Date(formData.startTime);
      const endDateTime = new Date(formData.endTime);
      
      if (startDateTime >= endDateTime) {
        newErrors.endTime = "End date/time must be after start date/time.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleReviewVoucher = () => {
    if (validateForm()) {
      setIsReviewModalOpen(true);
    }
  };

  const handleConfirmReview = () => {
    submit();
    setIsReviewModalOpen(false);
  };

  const handleBackReview = () => {
    setIsReviewModalOpen(false);
  };

  const cancel = async () => {
    const confirmLeave: any = await alertMessageWithCancel(
      ALERT_TITLES.INFO,
      "Are you sure to cancel your creating or editing discount voucher?"
    );
    if (confirmLeave) {
      navigate(ROUTES.DISCOUNT_VOUCHERS);
    }
  };

  return (
    <>
      {loading && (
        <LoadingOverlay>
          <LoadingSpinner>
            <LoadingSpinnerIcon />
          </LoadingSpinner>
        </LoadingOverlay>
      )}

      <CreateVoucherContainer>
        <PageTitle>
          <LanguageText>Create Voucher </LanguageText>
        </PageTitle>
        <FormContainer>
          <FormField>
            <FormLabel>Voucher Name*</FormLabel>
            <FormInput
              type="text"
              placeholder="Happy Hour"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              $hasError={!!errors.name}
            />
            {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
          </FormField>

          <DiscountTypeSection>
            <FormLabel>Discount Type</FormLabel>
            <DiscountTypeButtons>
              <DiscountTypeButton
                $active={formData.type === "amount"}
                onClick={() => handleDiscountTypeChange("amount")}
              >
                Fixed amount discount
              </DiscountTypeButton>
              <DiscountTypeButton
                $active={formData.type === "percentage"}
                onClick={() => handleDiscountTypeChange("percentage")}
              >
                Percentage discount
              </DiscountTypeButton>
            </DiscountTypeButtons>
          </DiscountTypeSection>

          <FlexContainerResponsive>
            {/* Discount Value */}
            <FormField>
              <FormLabel>Discount Value*</FormLabel>
              <FormInput
                type="number"
                step="0.01"
                pattern="[0-9]+(\.[0-9]{1,2})?"
                placeholder={formData.type === "amount" ? "$2.80" : "10%"}
                value={formData.amount}
                onChange={(e) => handleInputChange("amount", e.target.value)}
                onInput={(e) => {
                  const target = e.target as HTMLInputElement;
                  const validatedValue = validateDecimalInput(target.value);
                  if (target.value !== validatedValue) {
                    target.value = validatedValue;
                  }
                }}
                $hasError={!!errors.amount}
              />
              {errors.amount && <ErrorMessage>{errors.amount}</ErrorMessage>}
            </FormField>

            {/* Minimum Order Value */}
            <FormField>
              <FormLabel>Minimum Order Value *</FormLabel>
              <FormInput
                type="number"
                step="0.01"
                pattern="[0-9]+(\.[0-9]{1,2})?"
                placeholder="Minimum amount spend to use voucher eg. $10"
                value={formData.miniumAmount}
                onChange={(e) =>
                  handleInputChange("miniumAmount", e.target.value)
                }
                onInput={(e) => {
                  const target = e.target as HTMLInputElement;
                  const validatedValue = validateDecimalInput(target.value);
                  if (target.value !== validatedValue) {
                    target.value = validatedValue;
                  }
                }}
                $hasError={!!errors.miniumAmount}
              />
              {errors.miniumAmount && (
                <ErrorMessage>{errors.miniumAmount}</ErrorMessage>
              )}
            </FormField>
          </FlexContainerResponsive>

          <FlexContainerResponsive>
            {/* Start Date/Time */}
            <FormField>
              <FormLabel>Start Date/Time</FormLabel>
              <VoucherDateTime
                placeholder="Choose Start Date/Time"
                value={formData.startTime}
                onChange={(value) =>
                  handleDateTimeInputChange("startTime", value)
                }
                disablePast={true}
                error={!!errors.startTime || !!pastDateWarning.startTime}
                helperText={errors.startTime || pastDateWarning.startTime}
              />
            </FormField>

            {/* End Date/Time */}
            <FormField>
              <FormLabel>End Date/Time</FormLabel>
              <VoucherDateTime
                placeholder="Choose End Date/Time"
                value={formData.endTime}
                onChange={(value) =>
                  handleDateTimeInputChange("endTime", value)
                }
                minDateTime={formData.startTime || getMinDateTime()}
                disablePast={true}
                error={!!errors.endTime}
                helperText={errors.endTime || pastDateWarning.endTime}
              />
            </FormField>
          </FlexContainerResponsive>

          <ActionButtons>
            <SendButton onClick={cancel}>
              <LanguageText>Back</LanguageText>
            </SendButton>

            {/* Review Button */}
            <ReviewButton onClick={handleReviewVoucher}>
              Review Voucher
            </ReviewButton>
          </ActionButtons>
        </FormContainer>

        {isReviewModalOpen && (
          <ReviewModalOverlay $isOpen={isReviewModalOpen}>
            <ReviewModalContent>
              <ReviewModalHeader>
                <ReviewModalTitle>Review Voucher</ReviewModalTitle>
                <ReviewModalCloseButton onClick={handleBackReview}>
                  x
                </ReviewModalCloseButton>
              </ReviewModalHeader>
              <ReviewSection>
                <ReviewSectionTitle>Voucher Details</ReviewSectionTitle>
                <ReviewDetailRow>
                  <ReviewDetailLabel>Voucher Name:</ReviewDetailLabel>
                  <ReviewDetailValue>{formData.name}</ReviewDetailValue>
                </ReviewDetailRow>
                <ReviewDetailRow>
                  <ReviewDetailLabel>Discount Type:</ReviewDetailLabel>
                  <ReviewDetailValue>
                    {formData.type === "amount"
                      ? "Fixed amount discount"
                      : "Percentage discount"}
                  </ReviewDetailValue>
                </ReviewDetailRow>
                <ReviewDetailRow>
                  <ReviewDetailLabel>Discount Value:</ReviewDetailLabel>
                  <ReviewDetailValue>
                    {formData.type === "amount"
                      ? `$${formData.amount}`
                      : `${formData.amount}%`}
                  </ReviewDetailValue>
                </ReviewDetailRow>
                <ReviewDetailRow>
                  <ReviewDetailLabel>Minimum Order Value:</ReviewDetailLabel>
                  <ReviewDetailValue>
                    ${formData.miniumAmount}
                  </ReviewDetailValue>
                </ReviewDetailRow>
                <ReviewDetailRow>
                  <ReviewDetailLabel>Start Date/Time:</ReviewDetailLabel>
                  <ReviewDetailValue>
                    {moment(formData.startTime).format("DD MMM YYYY h:mm A")}
                  </ReviewDetailValue>
                </ReviewDetailRow>
                <ReviewDetailRow>
                  <ReviewDetailLabel>End Date/Time:</ReviewDetailLabel>
                  <ReviewDetailValue>
                    {moment(formData.endTime).format("DD MMM YYYY h:mm A")}
                  </ReviewDetailValue>
                </ReviewDetailRow>
              </ReviewSection>
              <ReviewModalActions>
                <ReviewBackButton onClick={handleBackReview}>
                  Back
                </ReviewBackButton>
                <ReviewConfirmButton onClick={handleConfirmReview}>
                  Confirm and Save
                </ReviewConfirmButton>
              </ReviewModalActions>
            </ReviewModalContent>
          </ReviewModalOverlay>
        )}
      </CreateVoucherContainer>
    </>
  );
};

export default CreateVoucher;
